import { createBrowserRouter, redirect, Navigate } from "react-router-dom";
import PublicLayout from "../ui/layouts/public.layout";
import PrivateLayout from "../ui/layouts/private.layout";
import ErrorPage from "../pages/error-page/ui/error.page";
import { ChatPage } from "~/pages/chat/ui/chat.page";
import { LoginPage } from "../pages/auth/login/ui/login.page";
import { ForgotPasswordForm as ForgotPasswordPage } from "~/features/forgot-password/ui/forgot-password-form-client";
import { DashboardPage } from "../pages/dashboard/ui/dashboard.page";
import { MonitorPage } from "../pages/monitoring/ui/monitor.page";
import { AnalyticsPage } from "../pages/analytics/ui/analytics.page";
import { MvoAnalyticsSectionDetailPage } from "../pages/analytics/ui/mvo-analytics-section-detail.page";
import { TempFilterPage } from "~/pages/temp/ui/temp.page";
import { RecordsPage } from "../pages/records/ui/records.page";
import { GpsTrackingPage } from "../pages/gps-tracking/ui/gps-tracking.page";
import ProfilePage from "../pages/profile/profile.page";
import { AUTH_STORAGE } from "../shared/api/auth/auth.api";

function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_STORAGE.token);
  } catch {
    return null;
  }
}

async function rootLoader() {
  const token = getAuthToken();

  if (!token) return redirect("/login");
  return redirect("/dashboard");
}

async function redirectIfAuthed() {
  const token = getAuthToken();
  if (!token) return null;
  return redirect("/dashboard");
}

async function requireAuth() {
  const token = getAuthToken();
  if (!token) return redirect("/login");
  return null;
}

export const router = createBrowserRouter([
  {
    path: "/",
    loader: rootLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: "/impersonate",
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const token = url.searchParams.get("token");
      const user = url.searchParams.get("user");

      if (token) {
        localStorage.clear();
        sessionStorage.clear();

        localStorage.setItem(AUTH_STORAGE.token, token);
        if (user) {
          localStorage.setItem(AUTH_STORAGE.user, user);
        }

        if ("caches" in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }

        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          for (const reg of regs) await reg.unregister();
        }

        window.location.href = "/dashboard";
        return null;
      }
      return redirect("/login");
    },
  },
  // unauthenticated routes
  {
    element: <PublicLayout />,
    loader: redirectIfAuthed,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPasswordPage />,
      },
    ],
  },

  // authenticated routes
  {
    element: <PrivateLayout />,
    loader: requireAuth,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: "dashboard",
        element: <DashboardPage />,
      },
      {
        path: "monitor",
        element: <MonitorPage />,
      },
      {
        path: "analytics",
        element: <AnalyticsPage />,
      },
      {
        path: "analytics/section/:analyticsType",
        element: <MvoAnalyticsSectionDetailPage />,
      },
      {
        path: "temp-filter",
        element: <TempFilterPage />,
      },
      {
        path: "chat",
        element: <ChatPage />,
      },
      {
        path: "records/:recordType?/:selectedRecordId?",
        element: <RecordsPage />,
      },
      {
        path: "gps-tracking",
        element: <GpsTrackingPage />,
      },
      {
        path: "profile",
        element: <ProfilePage />,
      },
    ],
  },

  { path: "*", element: <ErrorPage /> },
]);
